<script lang="ts" setup>
import type { Entity } from '@/modules/Project/types'
import type { Project } from '@/modules/Projects/useProjects'
import { getHubFileFields } from '@/modules/Workspaces/KnowledgeHub/Hubs/getHubFileFields'
import DotIndicator from '@/uiKit/DotIndicator.vue'
import { computed, toRef } from 'vue'
import { useHubEntityStatus } from './useHubEntityStatus'

const props = defineProps<{
  fileProject: Project
  entity: Entity
}>()

const hubFields = computed(() => {
  return getHubFileFields({ properties: props.fileProject.properties, entity: props.entity })
})

const { status, dotIndicatorReason, recompute, hasFieldErrors } = useHubEntityStatus(
  hubFields,
  toRef(props.fileProject.id),
)

const finalStatus = computed(() => {
  // Only show as ready if there's a summary and status is idle
  if (status.value === 'idle' && hubFields.value.summary.toolValue) {
    return 'ready'
  }
  return status.value
})

const finalDotIndicatorReason = computed(() => {
  // Show success indicator for ready state
  if (finalStatus.value === 'ready') {
    return { color: 'green', tooltip: undefined } as const
  }
  return dotIndicatorReason.value
})

const statusText = computed(() => {
  switch (finalStatus.value) {
    case 'error':
      return 'Error'
    case 'processing':
      return 'Processing…'
    case 'ready':
      return 'Ready'
    case 'idle':
      return ''
    default:
      return 'Unknown'
  }
})
</script>

<template>
  <div
    v-if="finalStatus !== 'idle'"
    class="flex items-center gap-1"
  >
    <DotIndicator
      v-if="finalDotIndicatorReason"
      :color="finalDotIndicatorReason.color"
      :tooltip="{ ...finalDotIndicatorReason.tooltip, placement: 'top' }"
      :clickable="hasFieldErrors"
      @click="recompute"
    />
    <span class="text-xs text-text-subtle">
      {{ statusText }}
    </span>
  </div>
</template>
